import React, { useState, useRef, useEffect, useCallback, createContext, useContext } from 'react';
import styled from 'styled-components';
import { Border, Color, Grid, Transition, Type } from '../app/StyleGuide';
import { createPortal } from 'react-dom';

const DropdownContext = createContext<{ close: () => void } | null>(null);

const Dropdown = styled.div<{ placement?: 'top' | 'bottom' | 'left' | 'right' }>`
  position: relative;
  display: inline-block;
`;

const DropdownMenu = styled.div<{ isOpen: boolean; placement?: 'top' | 'bottom' | 'left' | 'right' }>`
  background: ${Color.white};
  border: 1px solid ${Color.Gray._20};
  border-radius: ${Border.radius};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-width: 220px;
  z-index: 1000;
  display: ${props => (props.isOpen ? 'block' : 'none')};
  margin: ${props => {
    switch (props.placement) {
      case 'top':
        return `0 0 ${Grid._2} 0`;
      case 'bottom':
        return `${Grid._2} 0 0 0`;
      case 'left':
        return `0 ${Grid._2} 0 0`;
      case 'right':
        return `0 0 0 ${Grid._2}`;
      default:
        return `${Grid._2} 0 0 0`;
    }
  }};
`;

type DropdownItemProps = {
  onClick?: (event: React.MouseEvent) => void;
};

const StyledDropdownItem = styled.div`
  padding: ${Grid._3} ${Grid._4};
  cursor: pointer;
  transition: ${Transition.fast};
  color: ${Type.Color.dark};
  font-size: ${Type.Scale._2};
  display: flex;
  align-items: center;
  gap: ${Grid._2};

  &:hover {
    background: ${Color.Gray._05};
  }

  i {
    margin-right: ${Grid._2};
  }
`;

export const DropdownItem = ({ onClick, ...props }: DropdownItemProps & React.HTMLAttributes<HTMLDivElement>) => {
  const context = useContext(DropdownContext);

  const handleClick = (e: React.MouseEvent) => {
    onClick?.(e);
    if (context) {
      setTimeout(() => context.close(), 0);
    }
  };

  return <StyledDropdownItem {...props} onClick={handleClick} role="menuitem" />;
};

type DropdownProps = {
  placement?: 'top' | 'bottom' | 'left' | 'right';
  trigger: React.ReactNode;
  children: React.ReactNode;
};

export default function DropdownComponent({ placement = 'bottom', trigger, children }: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isInsideDropdown = dropdownRef.current?.contains(target);
      const isInsideMenu = menuRef.current?.contains(target);

      if (!isInsideDropdown && !isInsideMenu) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const updatePosition = useCallback(() => {
    if (dropdownRef.current) {
      const rect = dropdownRef.current.getBoundingClientRect();

      let top = 0;
      let left = 0;

      switch (placement) {
        case 'top':
          top = rect.top - parseInt(Grid._2);
          left = rect.left;
          break;
        case 'bottom':
          top = rect.bottom + parseInt(Grid._2);
          left = rect.left + rect.width / 2;
          break;
        case 'left':
          top = rect.top;
          left = rect.left - parseInt(Grid._2);
          break;
        case 'right':
          top = rect.top;
          left = rect.right + parseInt(Grid._2);
          break;
        default:
          top = rect.bottom + parseInt(Grid._2);
          left = rect.left;
      }

      setMenuPosition({ top, left });
    }
  }, [placement]);

  useEffect(() => {
    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition);
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition);
      };
    }
  }, [isOpen, updatePosition]);

  const handleMenuClick = (event: React.MouseEvent) => {
    // If clicked directly on the menu container (empty space), close immediately
    const target = event.target as HTMLElement;
    if (target === menuRef.current) {
      setIsOpen(false);
    }
  };

  return (
    <>
      <Dropdown placement={placement} ref={dropdownRef}>
        <div onClick={() => setIsOpen(!isOpen)} role="menu">
          {trigger}
        </div>
      </Dropdown>
      {isOpen &&
        (createPortal(
          <DropdownContext.Provider value={{ close: () => setIsOpen(false) }}>
            <DropdownMenu
              ref={menuRef}
              isOpen={isOpen}
              placement={placement}
              onClick={handleMenuClick}
              style={{
                position: 'fixed',
                top: menuPosition.top,
                left: menuPosition.left,
                transform:
                  placement === 'bottom'
                    ? 'translateX(-50%)'
                    : placement === 'left'
                    ? 'translateX(-100%)'
                    : placement === 'top'
                    ? 'translateY(-100%)'
                    : 'none',
              }}
            >
              {children}
            </DropdownMenu>
          </DropdownContext.Provider>,
          document.body
        ) as React.ReactPortal)}
    </>
  );
}
